/**
 * useMessages
 * Fetches, paginates, and polls messages for a single conversation.
 *
 * Accepts an optional `service` parameter for dependency injection.
 * Default: chatService (regular user). Admin can pass adminChatService.
 *
 * Critical bug fixes applied:
 *
 * 1. POLLING DEPENDENCY LOOP PREVENTED:
 *    messagesRef mirrors the messages state array. The pollMessages
 *    callback reads from messagesRef.current — never from the messages
 *    state variable itself. This means messages state is NOT in the
 *    pollMessages useCallback dep array, so the callback is stable and
 *    the polling interval never restarts on every new message.
 *
 * 2. LOADING STATE: initialized as false, set true only AFTER the
 *    guard clause (`if (!conversationId) return`). Prevents permanent
 *    spinner when conversationId is absent.
 *
 * 3. EMPTY CONVERSATION POLLING: No guard on messagesRef.current.length.
 *    Polling fires even on empty conversations so the first incoming
 *    message from the other participant always appears.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { chatService as defaultService } from '../services/chatService';

const POLL_INTERVAL_MS = 10_000;

/**
 * @param {string|null} conversationId
 * @param {object} [service] - Service with getMessages/sendMessage/deleteMessage/markAsRead. Defaults to chatService.
 * @returns {{
 *   messages: Message[],
 *   loading: boolean,
 *   error: string|null,
 *   hasMore: boolean,
 *   sending: boolean,
 *   loadMore: () => Promise<void>,
 *   sendMessage: (content: string, replyToId?: string|null) => Promise<Message>,
 *   deleteMessage: (messageId: string) => Promise<void>,
 *   markAsRead: () => Promise<void>
 * }}
 */
export default function useMessages(conversationId, service = defaultService) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false); // false — not true
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);
  const [sending, setSending] = useState(false);

  /**
   * Mirror of the messages state.
   * pollMessages reads from here so messages state never ends up in
   * the useCallback dep array (which would cause the polling loop bug).
   */
  const messagesRef = useRef([]);

  // ─── helpers ────────────────────────────────────────────────────────────────

  function applyMessages(newMsgs) {
    setMessages(newMsgs);
    messagesRef.current = newMsgs;
  }

  // ─── initial load ────────────────────────────────────────────────────────────

  const loadMessages = useCallback(async () => {
    if (!conversationId) return;   // guard first
    setLoading(true);              // set true only after guard
    setError(null);
    try {
      const result = await service.getMessages(conversationId, { limit: 30 });
      applyMessages(result.messages);
      setHasMore(result.hasMore);
      setNextCursor(result.nextCursor);
    } catch (err) {
      setError(err.message || 'Berichten konden niet worden geladen');
    } finally {
      setLoading(false);
    }
  }, [conversationId, service]);

  // ─── cursor-based pagination (scroll upward) ─────────────────────────────────

  const loadMore = useCallback(async () => {
    if (!hasMore || !nextCursor || !conversationId) return;
    try {
      const result = await service.getMessages(conversationId, {
        cursor: nextCursor,
        limit: 30,
      });
      // Prepend older messages, preserving existing ones at the end
      const merged = [...result.messages, ...messagesRef.current];
      applyMessages(merged);
      setHasMore(result.hasMore);
      setNextCursor(result.nextCursor);
    } catch (err) {
      setError(err.message || 'Oudere berichten konden niet worden geladen');
    }
  }, [conversationId, hasMore, nextCursor, service]);

  // ─── polling (10 s) ──────────────────────────────────────────────────────────

  /**
   * Fetches the latest page and merges any new messages into the list.
   *
   * KEY: messagesRef.current is read inside the function body but is NOT
   * listed in the dependency array. This keeps the callback reference stable
   * so setInterval(pollMessages, 10_000) never restarts after a new message
   * arrives — which was the root cause of the polling loop bug.
   *
   * conversationId IS in deps because it is a primitive string. When the
   * conversation changes the callback is safely recreated.
   */
  const pollMessages = useCallback(async () => {
    if (!conversationId) return;
    try {
      const result = await service.getMessages(conversationId, { limit: 30 });

      // Merge: dedup by id to avoid duplicates
      const existingIds = new Set(messagesRef.current.map(m => m.id));
      const newMsgs = result.messages.filter(m => !existingIds.has(m.id));

      // Also refresh read/deleted state of messages already in the list
      const refreshed = messagesRef.current.map(m => {
        const fresh = result.messages.find(fm => fm.id === m.id);
        return fresh ? fresh : m;
      });

      const merged = newMsgs.length > 0
        ? [...refreshed, ...newMsgs]
        : refreshed;

      applyMessages(merged);
    } catch {
      // Silent — polling failures must not show error UI
    }
  }, [conversationId, service]); // ← messages state intentionally NOT here

  // ─── polling setup ────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!conversationId) return;
    loadMessages();
    const intervalId = setInterval(pollMessages, POLL_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [conversationId, loadMessages, pollMessages]);

  // ─── actions ─────────────────────────────────────────────────────────────────

  /**
   * Send a new message and optimistically append it.
   * @param {string} content
   * @param {string|null} replyToId
   * @returns {Promise<Message>}
   */
  const sendMessage = useCallback(async (content, replyToId = null) => {
    setSending(true);
    try {
      const newMsg = await service.sendMessage(conversationId, { content, replyToId });
      const updated = [...messagesRef.current, newMsg];
      applyMessages(updated);
      return newMsg;
    } finally {
      setSending(false);
    }
    // Throws on error — let the component handle the UI feedback
  }, [conversationId, service]);

  /**
   * Soft-delete a message. Optimistically updates deletedAt in state.
   * @param {string} messageId
   */
  const deleteMessage = useCallback(async (messageId) => {
    await service.deleteMessage(messageId);
    const updated = messagesRef.current.map(m =>
      m.id === messageId ? { ...m, deletedAt: new Date().toISOString(), content: null } : m
    );
    applyMessages(updated);
  }, [service]);

  /**
   * Mark all unread messages in the conversation as read.
   */
  const markAsRead = useCallback(async () => {
    if (!conversationId) return;
    try {
      await service.markAsRead(conversationId);
    } catch {
      // Silent — read receipts failing must not interrupt the UI
    }
  }, [conversationId, service]);

  return {
    messages,
    loading,
    error,
    hasMore,
    sending,
    loadMore,
    sendMessage,
    deleteMessage,
    markAsRead,
  };
}
